<template>
  <div class="pagination my-[3.5714rem] flex flex-row items-center justify-center">
    <NuxtLink
      aria-label="Previous page"
      v-if="!isFirstPage"
      :to="{ query: { ...route.query, page: currentPage - 1 } }"
      :class="prevButtonClasses"
      :id="`${paginationId}-previous`"
      external
    >
      <div :class="arrowIconClasses(false)">
        <Icon icon="arrow-left" :class="iconClasses(false)" />
      </div>
    </NuxtLink>

    <!-- Disabled Prev -->
    <div
      v-else
      :class="prevButtonClasses"
      aria-disabled="true"
      aria-label="Previous page"
      tabindex="-1"
      :id="`${paginationId}-disabled-previous`"
    >
      <div :class="arrowIconClasses(true)">
        <Icon icon="arrow-left" :class="iconClasses(true)" />
      </div>
    </div>

    <!-- Page Numbers -->
    <ul
      v-if="props.showPageNumbers && props.totalPages && props.totalPages >= 1 && props.totalCount && props.totalCount > 0"
      class="paginationList flex flex-row"
    >
      <li v-for="(page, index) in visiblePages" :key="`page-${index}`">
        <NuxtLink
          :aria-label="initialPage == page ? `Current page, Page ${page}` :`Go to page ${page}`"
          v-if="typeof page === 'number'"
          :to="{ query: { ...route.query, page } }"
          :class="pageClasses(page)"
          :id="`${paginationId}-${index + 1}`"
          external
          :aria-current="page === currentPage ? 'page' : undefined"
        >
          {{ page }}
        </NuxtLink>

        <span
          v-else
          class="mx-[0.25rem] px-[0.5rem] py-1.5 text-gray-900
                 min-w-[2.5rem] h-[2.5rem] text-sm flex items-center justify-center"
        >
          {{ page }}
        </span>
      </li>
    </ul>

    <!-- Next (Right) -->
    <NuxtLink
      aria-label="Next page"
      v-if="!isLastPage"
      :to="{ query: { ...route.query, page: currentPage + 1 } }"
      :class="nextButtonClasses"
      :id="`${paginationId}-next`"
      external
    >
      <div :class="arrowIconClasses(false)">
        <Icon icon="arrow-right" :class="iconClasses(false)"/>
      </div>
    </NuxtLink>

    <!-- Disabled Next -->
    <div
      v-else
      :class="nextButtonClasses"
      aria-disabled="true"
      aria-label="Next page"
      tabindex="-1"
      :id="`${paginationId}-disabled-next`"
    >
      <div :class="arrowIconClasses(true)">
        <Icon icon="arrow-right" :class="iconClasses(true)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import Icon from '../atom/icon.vue'
import type { PaginationProps } from '~~/shared/types/common'

const props = withDefaults(defineProps<PaginationProps>(), {
  totalPages: undefined,
  initialPage: 1,
  showPageNumbers: true,
  totalCount: undefined,
  hasNext: undefined,
  hasPrev: undefined,
  limit: 10,
  id:'pagination'
})

const route = useRoute()

const currentPage = computed(() => Number(props.initialPage) || 1)

const paginationId = computed(() => props.id || 'pagination')

const visiblePages = computed(() => {
  const total = props.totalPages ?? 1
  const pages: (number | string)[] = []

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i)
  } else if (currentPage.value <= 4) {
    pages.push(1, 2, 3, 4, "...", total)
  } else if (currentPage.value >= total - 3) {
    pages.push(1, "...", total - 3, total - 2, total - 1, total)
  } else {
    pages.push(1, "...", currentPage.value - 1, currentPage.value, currentPage.value + 1, "...", total)
  }

  return pages
})

const isFirstPage = computed(() => {
  if (props.hasPrev !== undefined) return !props.hasPrev
  return currentPage.value === 1
})

const isLastPage = computed(() => {
  if (props.hasNext !== undefined) return !props.hasNext
  if (props.totalPages) return currentPage.value >= props.totalPages
  if (props.totalCount !== undefined && props.limit !== undefined) {
    return (props.totalCount as number) < (props.limit as number)
  }
  return false
})

const prevButtonClasses = computed(() => [
  "px-0 md:px-[2.1429rem] py-[0.4286rem] h-[3.5714rem] mx-1 md:mx-[0.25rem] flex items-center group",
  isFirstPage.value ? "pointer-events-none opacity-50" : ""
])

const nextButtonClasses = computed(() => [
  "px-0 md:px-[2.1429rem] py-[0.4286rem] h-[3.5714rem] mx-1 md:mx-[0.25rem] flex items-center group",
  isLastPage.value ? "pointer-events-none opacity-50" : ""
])

const baseArrowIcon =
  "arrowIcon border-[0.2143rem] border-solid rounded-full w-[3.5714rem] h-[3.5714rem] flex items-center justify-center"

const arrowIconClasses = (isDisabled: boolean) => [
  baseArrowIcon,
  isDisabled ? "text-gray-600" : "text-gray-900 group-hover:text-gold-100"
]

const iconClasses = (isDisabled: boolean) => [
  "mt-0 text-[2.1429rem]",
  isDisabled ? "text-gray-600" : "text-black group-hover:text-gold-100"
]

const pageClasses = (page: number) => [
  "mx-[0.25rem] px-[0.5rem] py-1.5 min-w-[2.5rem] h-[2.5rem] text-sm flex items-center justify-center rounded-md",
  page === currentPage.value
    ? "text-white bg-gray-900 active-page"
    : "text-gray-900 hover:text-gold-100"
]
</script>
